import * as React from 'react';
import * as styles from "./MediaSelector.css";
import { LocalizationService } from '../../services/LocalizationService';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IAOptionSelector } from '../optionSelector/OptionSelector';
import { IAMediaSelectorUpload } from './mediaSelectorUpload/MediaSelectorUpload';
import { IAMediaSelectorPreview } from './mediaSelectorPreview/MediaSelectorPreview';
import { IAOrganizationalAssetPanel } from '../composer/organizationalAssetPanel/OrganizationalAssetPanel';
import { StockPhotosPanel } from '../stockPhotos/StockPhotosPanel';
import { IAMediaSelectorLink } from './mediaSelectorLink/MediaSelectorLink';
import { Helper } from '../../Helper';
import { ICropSetting } from '../../interfaces/ICropSetting';

export interface IAOrgAssetsUploaderProps {
  showPreview: boolean;
  token: string;
  tenant: string;
  uploadUrl: string;
  environment: IEnvironment;
  highlightColor: string;
  selectedImage: string;
  selectedImageCrop?: ICropSetting;
  label?: string;
  isMobile?: boolean;
  fileType: "image" | "video";
  style?: React.CSSProperties;
  panelTop?: number;
  onChange: (image: string) => void;
}

export interface IAOrgAssetsUploaderState {
  selectedOption: IAMediaSelectorPanelOptions;
}

export type IAMediaSelectorPanelOptions = "Upload" | "Link" | "OrganizationalAssets" | "StockPhotos";

export class IAMediaSelector extends React.Component<IAOrgAssetsUploaderProps, IAOrgAssetsUploaderState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: IAOrgAssetsUploaderProps) {
    super(props);
    this.state = {
      selectedOption: undefined
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private closePanels(){
    this.setState({selectedOption: undefined});
  }

  private setImage(url: string){
    this.closePanels();
    this.props.onChange(url);
  }

  public render(): JSX.Element {
    const isLinkSource = this.state.selectedOption === "Link";
    const isUploadSource = this.state.selectedOption === "Upload";
    const style = this.props.style ?? {};

    let panelMargin = 0;
    const displayEnv = Helper.getDisplayEnvironment();
    if ((displayEnv === "Sharepoint" || displayEnv === "SharepointClassic") && document.getElementById("IA_navigator_bar") && !document.getElementById("IA_navigator_vertical_menu")) {
      panelMargin = 88;
    }
    if ((displayEnv === "Sharepoint" || displayEnv === "SharepointClassic") && document.getElementById("IA_navigator_vertical_menu")) {
      panelMargin = 48;
    }
    if (document.getElementById("IA_navigator_bar") && !document.getElementById("IA_navigator_vertical_menu")) {
      panelMargin = 40;
    }
    if (location.href.indexOf("intraactive-admin") !== -1) {
      panelMargin = 109;
    }

    return (
      <>
        <div
          className={styles.IA_MediaSelectorContainer}
          style={style}
        >
          {this.props.label && <label style={{ marginBottom: 10, fontSize: 14 }}>{this.props.label}</label>}
          {this.props.selectedImage ?
            <IAMediaSelectorPreview
              token={this.props.token}
              tenant={this.props.tenant}
              environment={this.props.environment}
              highlightColor={this.props.highlightColor}
              fileType={this.props.fileType}
              url={this.props.selectedImage}
              imageCrop={this.props.selectedImageCrop}
              onClearImage={() => this.setImage("")}
            />
          :
            <>
              <IAOptionSelector
                style={{ marginTop: 0 }}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                    key: "Upload",
                    text: this.localizationService.strings.Composer_Upload
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                    key: "Link",
                    text: this.localizationService.strings.Composer_FromALink
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                    key: "OrganizationalAssets",
                    text: this.localizationService.strings.Composer_FromOrgAssets
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Image%20Search/SVG/ic_fluent_image_search_24_regular.svg",
                    key: "StockPhotos",
                    text: this.localizationService.strings.Composer_StockPhotos
                  }
                ]}
                selected={this.state.selectedOption}
                highlightColor={this.props.highlightColor}
                iconSize={35}
                onChange={(option: IAMediaSelectorPanelOptions) => {
                  if (this.state.selectedOption !== option) {
                    this.setState({selectedOption: option});
                  }
                }}
                borderRadius={5}
              />
              {isLinkSource &&
                <IAMediaSelectorLink
                  token={this.props.token}
                  tenant={this.props.tenant}
                  environment={this.props.environment}
                  highlightColor={this.props.highlightColor}
                  fileType={this.props.fileType}
                  onSelect={(url) => this.props.onChange(url)}
                />
              }
              {isUploadSource &&
                <IAMediaSelectorUpload
                  token={this.props.token}
                  tenant={this.props.tenant}
                  environment={this.props.environment}
                  highlightColor={this.props.highlightColor}
                  fileType={this.props.fileType}
                  uploadUrl={this.props.uploadUrl}
                  onSelect={(url) => this.props.onChange(url)}
                />
              }
            </>
          }
        </div>
         <IAOrganizationalAssetPanel
           token={this.props.token}
           tenant={this.props.tenant}
           isMobile={this.props.isMobile}
           environment={this.props.environment}
           highlightColor={this.props.highlightColor}
           open={this.state.selectedOption === "OrganizationalAssets"}
           marginTop={this.props.panelTop ?? panelMargin}
           onSelect={(url: string) => this.setImage(url)}
           close={() => this.closePanels()}
           fileTypeToDisplay={this.props.fileType}
         />
         <StockPhotosPanel
           highlightColor={this.props.highlightColor}
           open={this.state.selectedOption === "StockPhotos"}
           tenant={this.props.tenant}
           environment={this.props.environment}
           isMobile={this.props.isMobile}
           token={this.props.token}
           marginTop={this.props.panelTop ?? panelMargin}
           onSelect={(url: string) => this.setImage(url)}
           close={() => this.closePanels()}
         />
      </>
    );
  }
}

